import { createContext, FC, useContext, useMemo } from 'react'
import { useDebounce, WithChildren } from '../../../../../_metronic/helpers'
import { createListFilters } from '../../../../components'
import {
  AgrupamentoQuantitativo,
  FiltroRelatorios,
  ParametrosAnexoIi,
  ParametrosPeriodo,
} from './_models'
import { montaFiltroInicial, parametrosAnexoIi, parametrosPeriodo } from './filtro'

/**
 * Filtros da página de relatórios, compartilhados pela barra de filtros, pelas abas e pelas
 * exportações. "Limpar filtros" volta ao período inicial (mês corrente, competência anterior).
 */
const filtros = createListFilters<FiltroRelatorios>(montaFiltroInicial())
export const useListFilters = filtros.useListFilters

type ParametrosRelatorios = {
  periodo?: ParametrosPeriodo
  anexoIi?: ParametrosAnexoIi
  agrupar: AgrupamentoQuantitativo
}

const ParametrosContext = createContext<ParametrosRelatorios>({ agrupar: 'especialidade' })

/**
 * Parâmetros das rotas derivados dos filtros, calculados uma vez (um debounce só para a página
 * inteira). `undefined` enquanto faltar empresa ou o período/competência for inválido — é essa
 * guarda que impede requisição incompleta.
 */
const ParametrosProvider: FC<WithChildren> = ({ children }) => {
  const { filters } = useListFilters()
  // Digitar uma data não dispara uma requisição por tecla.
  const estaveis = useDebounce(filters, 500)

  const value = useMemo(
    () => ({
      periodo: parametrosPeriodo(estaveis),
      anexoIi: parametrosAnexoIi(estaveis),
      agrupar: estaveis.agrupar,
    }),
    [estaveis]
  )

  return <ParametrosContext.Provider value={value}>{children}</ParametrosContext.Provider>
}

export const ListFiltersProvider: FC<WithChildren> = ({ children }) => (
  <filtros.ListFiltersProvider>
    <ParametrosProvider>{children}</ParametrosProvider>
  </filtros.ListFiltersProvider>
)

export const useParametrosRelatorios = () => useContext(ParametrosContext)
